import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { hotMovies, latestMovies, tvSeries, categories } from '@/mocks/movies';
import type { MovieItem } from '@/mocks/movies';

const allMovies: MovieItem[] = [...hotMovies, ...latestMovies, ...tvSeries];

function getCategoryName(slug: string): string {
  const cat = categories.find((c) => c.slug === slug);
  return cat ? cat.name : slug;
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = allMovies.find((m) => m.id === Number(id));

  if (!movie) {
    return (
      <>
        <ScrollToTop />
        <Navbar alwaysShowBg />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-film-line text-3xl text-foreground-500"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-200 mb-2">影片未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该影片可能已下架或链接无效</p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-950 rounded-lg text-sm font-medium whitespace-nowrap transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedMovies = allMovies
    .filter((m) => m.id !== movie.id && m.category === movie.category)
    .slice(0, 6);

  const categoryName = getCategoryName(movie.category);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    '@id': `https://www.qiyoucinema.com/article/${movie.id}#movie`,
    'name': movie.title,
    'description': movie.description,
    'image': movie.imageUrl,
    'dateCreated': `${movie.year}`,
    'director': {
      '@type': 'Person',
      'name': movie.director,
    },
    'actor': movie.actors.map((a) => ({
      '@type': 'Person',
      'name': a,
    })),
    'aggregateRating': {
      '@type': 'AggregateRating',
      'ratingValue': movie.rating,
      'bestRating': '10',
      'ratingCount': parseInt(movie.views.replace(/[^0-9]/g, '')) || 1000,
    },
    'countryOfOrigin': {
      '@type': 'Country',
      'name': movie.region,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      {
        '@type': 'Question',
        'name': `${movie.title}可以在奇优影院免费观看吗？`,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': `是的，${movie.title}在奇优影院提供免费在线观看，无需注册会员，点击即可播放，享受秒开无卡顿的流畅观影体验。`,
        },
      },
      {
        '@type': 'Question',
        'name': `${movie.title}的画质如何？`,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': `${movie.title}支持${movie.quality}超清画质播放，奇优影院采用先进的视频编码技术，确保每一帧画面都清晰细腻，同时智能调节码率保证播放流畅不缓冲。`,
        },
      },
      {
        '@type': 'Question',
        'name': `奇优影院播放${movie.title}会卡顿吗？`,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': '奇优影院采用P2P-CDN混合加速技术，全网覆盖高速节点，确保观影过程秒开无卡顿。无论您是观看动作大片还是长剧集，都能享受流畅的播放体验。',
        },
      },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://www.qiyoucinema.com/' },
      { '@type': 'ListItem', 'position': 2, 'name': categoryName, 'item': `https://www.qiyoucinema.com/category/${movie.category}` },
      { '@type': 'ListItem', 'position': 3, 'name': movie.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <Navbar alwaysShowBg />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

      <main className="pt-16">
        <div className="w-full px-4 md:px-8 py-4 md:py-6 bg-background-100 border-b border-background-300/20">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: categoryName, path: `/category/${movie.category}` },
                { label: movie.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-8 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              <div className="flex-shrink-0 w-full lg:w-[300px]">
                <div className="relative overflow-hidden rounded-lg aspect-[2/3] bg-background-200 mb-4 sticky top-24">
                  <img
                    src={movie.imageUrl}
                    alt={`${movie.title}海报`}
                    title={`${movie.title} - 奇优影院高清在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 bg-primary-500/20 text-primary-400 rounded-md text-xs font-medium">
                    {categoryName}
                  </span>
                  <span className="px-2.5 py-1 bg-accent-500/20 text-accent-400 rounded-md text-xs font-medium">
                    {movie.quality}
                  </span>
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 bg-background-200/60 text-foreground-500 rounded-md text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-2xl md:text-4xl font-heading text-foreground-100 mb-2">
                  {movie.title}
                </h1>
                <p className="text-base md:text-lg text-foreground-300 mb-4">
                  {movie.subtitle}
                </p>

                <div className="flex flex-wrap items-center gap-3 md:gap-4 mb-6 text-sm text-foreground-400">
                  <span className="flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-amber-400"></i>
                    </span>
                    <strong className="text-foreground-200">{movie.rating}</strong> / 10
                  </span>
                  <span className="w-0.5 h-0.5 rounded-full bg-foreground-600"></span>
                  <span>{movie.year}</span>
                  <span className="w-0.5 h-0.5 rounded-full bg-foreground-600"></span>
                  <span>{movie.duration}</span>
                  <span className="w-0.5 h-0.5 rounded-full bg-foreground-600"></span>
                  <span>{movie.region}</span>
                  <span className="w-0.5 h-0.5 rounded-full bg-foreground-600"></span>
                  <span className="flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line"></i>
                    </span>
                    {movie.views}次播放
                  </span>
                </div>

                <div className="flex items-center gap-3 mb-8">
                  <button className="flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-950 rounded-lg text-base font-medium whitespace-nowrap transition-colors">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-play-fill text-xl"></i>
                    </span>
                    立即播放
                  </button>
                  <button className="flex items-center gap-2 px-6 py-3 bg-background-200/50 hover:bg-background-200 text-foreground-200 rounded-lg text-base font-medium whitespace-nowrap transition-colors border border-background-300/30">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-heart-line text-lg"></i>
                    </span>
                    收藏
                  </button>
                </div>

                <section className="mb-8">
                  <h2 className="text-lg font-heading text-foreground-100 mb-3">剧情简介</h2>
                  <p className="text-sm md:text-base text-foreground-400 leading-relaxed">
                    {movie.description}
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-lg font-heading text-foreground-100 mb-3">影片信息</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex gap-3 p-3 rounded-lg bg-background-100 border border-background-300/20">
                      <span className="text-sm text-foreground-500 flex-shrink-0 min-w-[56px]">导演</span>
                      <span className="text-sm text-foreground-200">{movie.director}</span>
                    </div>
                    <div className="flex gap-3 p-3 rounded-lg bg-background-100 border border-background-300/20">
                      <span className="text-sm text-foreground-500 flex-shrink-0 min-w-[56px]">主演</span>
                      <span className="text-sm text-foreground-200">{movie.actors.join(' / ')}</span>
                    </div>
                    <div className="flex gap-3 p-3 rounded-lg bg-background-100 border border-background-300/20">
                      <span className="text-sm text-foreground-500 flex-shrink-0 min-w-[56px]">地区</span>
                      <span className="text-sm text-foreground-200">{movie.region}</span>
                    </div>
                    <div className="flex gap-3 p-3 rounded-lg bg-background-100 border border-background-300/20">
                      <span className="text-sm text-foreground-500 flex-shrink-0 min-w-[56px]">时长</span>
                      <span className="text-sm text-foreground-200">{movie.duration}</span>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-lg font-heading text-foreground-100 mb-3">常见问题</h2>
                  <div className="space-y-2">
                    {faqSchema.mainEntity.map((faq) => (
                      <details
                        key={faq.name}
                        className="p-4 rounded-lg bg-background-100 border border-background-300/20 group"
                      >
                        <summary className="text-sm font-medium text-foreground-200 cursor-pointer list-none flex items-center justify-between">
                          {faq.name}
                          <span className="w-5 h-5 flex items-center justify-center text-foreground-500 group-open:rotate-180 transition-transform">
                            <i className="ri-arrow-down-s-line"></i>
                          </span>
                        </summary>
                        <p className="text-sm text-foreground-500 mt-3 leading-relaxed">
                          {faq.acceptedAnswer.text}
                        </p>
                      </details>
                    ))}
                  </div>
                </section>
              </div>
            </div>
          </div>
        </article>

        {relatedMovies.length > 0 && (
          <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-100 border-t border-background-300/20">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg md:text-xl font-heading text-foreground-100">
                  相关推荐
                </h2>
                <Link
                  to={`/category/${movie.category}`}
                  className="text-sm text-foreground-500 hover:text-primary-400 transition-colors whitespace-nowrap"
                >
                  查看更多
                  <span className="w-4 h-4 inline-flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-base"></i>
                  </span>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                {relatedMovies.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group block"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[2/3] bg-background-200 mb-2.5">
                      <img
                        src={related.imageUrl}
                        alt={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                        loading="lazy"
                      />
                      <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-background-950/70 backdrop-blur-sm text-primary-400 rounded text-xs font-medium flex items-center gap-0.5">
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-star-fill text-amber-400"></i>
                        </span>
                        {related.rating}
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-200 group-hover:text-primary-400 transition-colors line-clamp-1">
                      {related.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}